/** Trailing slash and trailing /api removed so paths like /api/templates/:id stay correct. */
export function normalizeApiRoot(raw) {
  let s = (raw || '').trim().replace(/\/+$/, '');
  if (/\/api$/i.test(s)) s = s.replace(/\/api$/i, '');
  return s;
}

/** Node flyer backend (templates, flyers, GCP images) — not the CFS gateway. */
const FLYER_API_PATH_RE = /^\/api\/(templates|flyers|gcp-images|fonts|health)(\/|$)/i;

/** CFS portal host → flyer API (Spring gateway on dev-cfs / qa-cfs). */
const BUILTIN_FLYER_API_HOST_MAP = {
  'dev-cfs.royalcyber.org': 'https://dev-cfs.royalcyber.org',
  'qa-cfs.royalcyber.org': 'https://qa-cfs.royalcyber.org',
};

export function isFlyerBackendPath(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  return FLYER_API_PATH_RE.test(p);
}

function runtimeFlyerHostMap() {
  if (typeof window === 'undefined') return BUILTIN_FLYER_API_HOST_MAP;
  const fromRuntime = window.__FLYER_API_HOST_MAP__;
  if (fromRuntime && typeof fromRuntime === 'object') {
    return { ...BUILTIN_FLYER_API_HOST_MAP, ...fromRuntime };
  }
  return BUILTIN_FLYER_API_HOST_MAP;
}

/** Runtime override from public/flyer-runtime-config.js (or K8s-mounted config). */
function runtimeFlyerApiRoot() {
  if (typeof window === 'undefined') return '';
  const explicit = window.__FLYER_API_ROOT__;
  if (explicit && typeof explicit === 'string' && explicit.trim()) {
    return normalizeApiRoot(explicit);
  }
  const host = window.location?.hostname || '';
  const mapped = runtimeFlyerHostMap()[host];
  return mapped ? normalizeApiRoot(mapped) : '';
}

/** Local Node API root; ignores REACT_APP_API_URL when it points at royalcyber.org (CFS). */
export function resolveFlyerApiRoot() {
  const flyer = normalizeApiRoot(process.env.REACT_APP_FLYER_API_URL || '');
  if (flyer) return flyer;
  const runtime = runtimeFlyerApiRoot();
  if (runtime) return runtime;
  const api = normalizeApiRoot(process.env.REACT_APP_API_URL || '');
  if (api && !/royalcyber\.org/i.test(api)) return api;
  return '';
}

/** Absolute URL for any app API path (flyer backend vs CFS gateway). */
export function resolveRequestUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  let root = '';
  if (isFlyerBackendPath(p)) {
    root = resolveFlyerApiRoot();
  } else {
    root = resolveApiRoot();
  }
  if (root) return `${root}${p}`;
  if (typeof window !== 'undefined') return `${window.location.origin}${p}`;
  return p;
}

let mixedContentApiWarned = false;

/**
 * When the SPA is served over HTTPS, the browser blocks `http://…` API URLs (mixed content).
 * In that case use same-origin `/api/…` so nginx (or similar) can reverse-proxy to the Node backend.
 */
export function resolveApiRoot() {
  const raw = normalizeApiRoot(process.env.REACT_APP_API_URL || '');
  if (
    typeof window !== 'undefined' &&
    window.location?.protocol === 'https:' &&
    raw &&
    /^http:\/\//i.test(raw)
  ) {
    if (!mixedContentApiWarned) {
      mixedContentApiWarned = true;
      console.warn(
        '[apiUrl] HTTPS page + HTTP API URL would be blocked (mixed content). Using same-origin /api/… — ensure this host proxies /api to your backend.',
      );
    }
    return '';
  }
  return raw;
}

export function apiUrl(path) {
  const p = path.startsWith('/') ? path : `/${path}`;
  if (isFlyerBackendPath(p)) {
    const root = resolveFlyerApiRoot();
    return root ? `${root}${p}` : p;
  }
  const root = resolveApiRoot();
  return root ? `${root}${p}` : p;
}
